/**
 * Defensive readers for untrusted project data.
 *
 * A `.dawproject` file can come from another machine, an older build, or a text
 * editor. Every field is read through these helpers so that a malformed value
 * degrades to a sane default and is reported as a warning, instead of throwing
 * halfway through a load and leaving the app without a project.
 */

export class LoadReport {
  readonly warnings: string[] = [];
  warn(message: string): void {
    if (this.warnings.length < 200) this.warnings.push(message);
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function readNumber(value: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function readInt(value: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  return Math.round(readNumber(value, fallback, min, max));
}

export function readBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Strips ASCII control characters. Written as an explicit scan rather than a
 * regex so the source file itself stays free of control-character literals.
 */
function stripControlCharacters(input: string): string {
  let out = '';
  for (const ch of input) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 32 && code !== 127) out += ch;
  }
  return out;
}

/** Reads a string, stripping control characters and capping its length. */
export function readString(value: unknown, fallback: string, maxLength = 512): string {
  if (typeof value !== 'string') return fallback;
  const cleaned = stripControlCharacters(value).trim().slice(0, maxLength);
  return cleaned.length > 0 ? cleaned : fallback;
}

export function readEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** Reads a `Record<string, number>` of parameter values, discarding junk keys. */
export function readNumberMap(value: unknown, maxEntries = 128): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isRecord(value)) return out;
  let count = 0;
  for (const [k, v] of Object.entries(value)) {
    if (count++ >= maxEntries) break;
    if (k.length === 0 || k.length > 64) continue;
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}

const HEX_COLOR = /^#[0-9a-fA-F]{3,8}$/;

export function readColor(value: unknown, fallback: string): string {
  return typeof value === 'string' && HEX_COLOR.test(value) ? value : fallback;
}

/** Ids are used as object keys and DOM attributes; keep them boring. */
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function readId(value: unknown): string | null {
  return typeof value === 'string' && ID_PATTERN.test(value) ? value : null;
}
